/* Подписка закончилась — кабинет только для просмотра (прототип: nav.js, roInit).
   Полоса под шапкой на каждой странице, кроме профиля (там то же — в карточке подписки). Смотреть можно всё:
   списки, карточки, календарь, тренировки, базы, поиск и фильтры. Всё, что создаёт или меняет данные,
   останавливается с подсказкой: поля не принимают ввод, перетаскивание выключено, кнопки действий
   («Создать», «Добавить», «Сохранить», «Удалить»…) приглушены и не срабатывают. Сервер такие запросы
   тоже отклоняет (trpc.ts, trainerProcedure) — здесь только чтобы тренер сразу видел, что недоступно. */
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMe } from '../lib/data';
import { dlong } from '../lib/format';
import { toast } from '../ui/Toast';

const RO_ACT =
  /(^|[\s+])(Создать|Добавить|Сохранить|Удалить|Убрать|Дублировать|Изменить|Задать|Получил оплату|Пригласить|Отправить|Опубликовать|Назначить|Вставить|Копировать|Скопировать в|Перенести|Заменить|Очистить|Принять|Выпустить|В тренировку|Отметить|Загрузить|Перетащить|Вернуть текст|Разобрать|Разгруппировать|Сообщение|Снять статус|Заморозить|Разморозить|Тренировка$|Отдых$)/i;
const RO_SEL = '[data-fee],[data-send],[data-delday],[data-u],.addb,.way4,.cph-up,.gr,.vid,.cdel,[draggable="true"]';
const CAND = 'button, a[href], [role=button], [role=switch], label, input[type=checkbox], input[type=radio], .addb, .way4, [data-fee], [data-u]';
const hint = () => toast('Подписка закончилась — продлите её в профиле, чтобы вносить изменения');
const on = () => document.body.classList.contains('subro');
/** Не трогаем: навигация, шапка, сама полоса, шторки меню, закрытие окон */
const free = (el: Element) => !!el.closest('.nav, .topbar, .roband, .msh, .mtabs, [data-ro-ok], [data-close], .cls, .mdh, .mx');
const search = (el: HTMLInputElement) =>
  el.type === 'search' || el.type === 'range' || /^(Поиск|Найти|Группа или участник)/.test(el.placeholder || '') || !!el.closest('.search, [class*=jump], .msort');
const editable = (el: Element) =>
  el.matches('textarea, select, [contenteditable="true"], [contenteditable=""]') || (el.matches('input') && !['button', 'submit', 'reset'].includes((el as HTMLInputElement).type));
const act = (t: Element) =>
  !free(t) &&
  (t.matches('a')
    ? RO_ACT.test((t.textContent || '').replace(/\s+/g, ' ').trim())
    : t.matches(RO_SEL) ||
      t.matches('input[type=checkbox], input[type=radio]') ||
      RO_ACT.test(((t.textContent || '') + ' ' + (t.getAttribute('aria-label') || '')).replace(/\s+/g, ' ').trim()) ||
      RO_ACT.test((t as HTMLElement).title || ''));

let installed = false;
function install() {
  if (installed) return;
  installed = true;
  document.addEventListener(
    'focusin',
    (e) => {
      const t = e.target as HTMLElement;
      if (!on() || !editable(t) || free(t) || search(t as HTMLInputElement)) return;
      t.blur();
      hint();
    },
    true,
  );
  document.addEventListener(
    'click',
    (e) => {
      if (!on()) return;
      const t = (e.target as Element).closest(CAND);
      if (t && act(t)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        hint();
      }
    },
    true,
  );
  document.addEventListener(
    'dragstart',
    (e) => {
      if (on() && !free(e.target as Element)) {
        e.preventDefault();
        hint();
      }
    },
    true,
  );
  /* Действия видно заранее: такие кнопки приглушены, подсказка — в title */
  const mark = () => {
    if (!on()) return;
    document.querySelectorAll(CAND).forEach((t) => {
      if (!t.classList.contains('ro-off') && act(t)) {
        t.classList.add('ro-off');
        (t as HTMLElement).title = 'Доступно после продления подписки';
      }
    });
  };
  new MutationObserver(mark).observe(document.body, { childList: true, subtree: true });
  mark();
}

/** Режим просмотра: класс body.subro (кроме профиля) и полоса под шапкой */
export function ReadOnlyBand() {
  const me = useMe().data;
  const { pathname } = useLocation();
  const ended = !!me?.sub.ended;
  const here = ended && !pathname.startsWith('/profile');
  useEffect(() => {
    if (here) install();
    document.body.classList.toggle('subro', here);
    if (!here) document.querySelectorAll('.ro-off').forEach((t) => t.classList.remove('ro-off'));
    return () => document.body.classList.remove('subro');
  }, [here]);
  if (!here || !me?.planUntil) return null;
  return (
    <div className="roband" role="status">
      <span className="t">
        <b>
          {me.sub.trial ? 'Пробный период закончился' : 'Подписка закончилась'} {dlong(me.planUntil).replace(/ \d{4}$/, '')}.
        </b>{' '}
        Кабинет только для просмотра, клиенты по-прежнему видят назначенные тренировки.
      </span>
      <Link className="btn sm" to="/profile#billing">
        Продлить подписку
      </Link>
    </div>
  );
}
